'use client';

import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  ArrowUp01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import {
  AgentActivityPill,
  Collapsible,
  IconButton,
  MetaDefault,
  MonoSmall,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import type { LiveTerminal } from './useLiveChatTerminals';

function LiveTerminalRow({ terminal }: { terminal: LiveTerminal }) {
  const outputRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(terminal.running);

  useEffect(() => {
    const node = outputRef.current;

    if (!node || !isExpanded) {
      return;
    }

    node.scrollTop = node.scrollHeight;
  }, [isExpanded, terminal.output]);

  return (
    <Collapsible.Root
      open={isExpanded}
      onOpenChange={({ open }) => setIsExpanded(open)}
    >
      <Collapsible.Trigger asChild>
        <HStack
          gap={2}
          alignItems="center"
          width="full"
          minW={0}
          px={2}
          py={1}
          cursor="pointer"
        >
          {isExpanded ? (
            <ArrowDown01Icon size={12} />
          ) : (
            <ArrowRight01Icon size={12} />
          )}
          <MetaDefault
            truncate
            color={terminal.running ? 'fg.default' : 'fg.muted'}
          >
            {terminal.title}
          </MetaDefault>
        </HStack>
      </Collapsible.Trigger>
      {isExpanded ? (
        <Box ref={outputRef} maxH="160px" overflowY="auto" px={2} pb={2}>
          <MonoSmall color="fg.muted" whiteSpace="pre-wrap">
            {terminal.output || ' '}
          </MonoSmall>
        </Box>
      ) : null}
    </Collapsible.Root>
  );
}

export function CopilotLiveTerminals({
  terminals,
}: {
  terminals: LiveTerminal[];
}) {
  const t = useTranslations('chat.copilot');
  const tCommon = useTranslations('common');
  const [isExpanded, setIsExpanded] = useState(false);
  const runningCount = terminals.length;

  if (runningCount === 0) {
    return null;
  }

  return (
    <>
      {isExpanded ? (
        <Box
          position="absolute"
          bottom="100%"
          left={0}
          right={0}
          mb={1}
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="lg"
          bg="bg.default"
          boxShadow="md"
          py={1}
          zIndex={2}
          animation="fade-in 0.15s ease-out"
        >
          <HStack
            px={2}
            py={1}
            gap={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <MetaDefault color="fg.muted">
              {t('terminalsRunning', { count: runningCount })}
            </MetaDefault>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={tCommon('close')}
              _hover={{ color: 'fg.default', bg: 'transparent' }}
              onClick={() => setIsExpanded(false)}
            >
              <Cancel01Icon size={12} />
            </IconButton>
          </HStack>
          <VStack alignItems="stretch" gap={0} width="full">
            {terminals.map((terminal) => (
              <LiveTerminalRow key={terminal.sessionId} terminal={terminal} />
            ))}
          </VStack>
        </Box>
      ) : null}
      <AgentActivityPill
        status="running"
        label={t('terminalsCount', { count: runningCount })}
        title={t('showTerminals')}
        testId="running-terminals-trigger"
        state={isExpanded ? 'open' : 'closed'}
        isExpanded={isExpanded}
        onClick={() => setIsExpanded((open) => !open)}
        trailing={
          <Box color="fg.muted" flexShrink={0} display="flex">
            {isExpanded ? (
              <ArrowDown01Icon size={12} />
            ) : (
              <ArrowUp01Icon size={12} />
            )}
          </Box>
        }
      />
    </>
  );
}
